import { Instance, SnapshotIn, types } from 'mobx-state-tree';

import { UserImageModel } from '../userImage';
import {
  DYNAMIC_MODAL_ACTIONS,
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
  MODAL_QUALITY_OPEN_POINTS,
} from './dynamicModalLayer.constants';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { CtasType } from '@photosi/rn-components/modals/dynamic-modals/modal-quality-alert/ModalQualityAlert.constants';
import { AnalyticsEvent } from 'src/_shared/analyticsEvents';
import { ProductModel } from '../product';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';

export type TDynamicModalLayerModel = typeof DynamicModalLayer;
export type DynamicModalLayerInstance = Instance<typeof DynamicModalLayer>;
export interface IDynamicModalLayerModelIn extends SnapshotIn<TDynamicModalLayerModel> {}

export type AttachEventParams = {
  onPressDeleteProduct?: () => void;
  onPressDiscardChanges?: () => void;
  onPressSaveChanges?: () => void;
  onPressContinueToCart?: () => void;
  onPressRemoveProducts?: () => void;
  onPressAddBox?: () => void;
  uploadMultiSourceCallback?: (params?: {
    files?: PickedImage[];
    isDragged?: boolean;
    isFromPersonalize?: boolean;
    sourceType?: string;
  }) => void;
  newUploadMultiSourceCallback?: (params?: {
    fileIds?: string[];
    isDragged?: boolean;
    isFromPersonalize?: boolean;
    sourceType?: string;
  }) => void;
  actionCallback?: (action: DYNAMIC_MODAL_ACTIONS) => void;
  amplitudeEvent?: AnalyticsEvent;
  afterInit?: () => void;
  onPressOptionCallback?: (option: string) => void;
};

export const DynamicModalLayer = types
  .model({
    id: types.identifier,
    originSectionName: types.maybe(types.enumeration(Object.values(SECTION_NAMES))), // when open a new modal, you can set the section where the modal was opened from.
    dynamicModalView: types.optional(
      types.enumeration(Object.values(DYNAMIC_MODAL_VIEWS)),
      DYNAMIC_MODAL_VIEWS.PrivacyPolicy,
    ),
    /* MODAL QUALITY ALERT */
    modalQualityCtas: types.optional(types.enumeration(Object.values(CtasType)), CtasType.IGNORE),
    modalQualitySuggestions: types.frozen(),
    qualityAlertUserImage: types.maybe(types.reference(UserImageModel)),
    modalQualityOpenPoint: types.optional(
      types.enumeration(Object.values(MODAL_QUALITY_OPEN_POINTS)),
      MODAL_QUALITY_OPEN_POINTS.photo,
    ),
    /* UPSELLING MODAL */
    upSellingModalData: types.maybe(types.frozen()),
    /* MODAL DELETE */
    productToDelete: types.maybe(types.reference(ProductModel)),
    thumbnailsToDelete: types.optional(types.array(types.string), []),
    /* MODAL UPLOAD PHOTO */
    isReplacingPhoto: types.optional(types.boolean, false),
    isUploadingPhoto: types.optional(types.boolean, false),
    selectedMenuItem: types.optional(
      types.enumeration(Object.values(SELECTED_MENU_ITEMS)),
      SELECTED_MENU_ITEMS.Computer,
    ),
    showDiscountTable: types.optional(types.boolean, false),
    /* MODAL UPLOAD PHOTO ERROR */
    uploadErrorData: types.maybe(types.frozen()),

    /* MODAL CONFIGURATION STEPS  */
    initialProductConfigurationId: types.optional(types.string, ''),
    groupCode: types.optional(types.string, ''),
    skipConfigurationSteps: types.optional(types.array(types.string), []),
    groupId: types.optional(types.string, ''),
    /* MODAL CUT */
    modalCutType: types.optional(types.string, ''),
    /* MODAL OVER LIMIT PRODUCT */
    productLimit: types.optional(types.number, 0),
    groupWithLimit: types.optional(types.string, ''),
  })
  .volatile(self => ({
    onPressDeleteProduct: () => {},
    /* MODAL SAVE CHANGES */
    onPressDiscardChanges: () => {},
    onPressSaveChanges: () => {},
    /* MODAL QUALITY ALERT */
    onPressContinueToCart: () => {},
    actionCallback: (action: DYNAMIC_MODAL_ACTIONS) => {},
    /* MODAL UPLOAD MULTISOURCE */
    uploadMultiSourceCallback: (params?: {
      files?: PickedImage[];
      isDragged?: boolean;
      isFromPersonalize?: boolean;
      sourceType?: string;
    }) => {},
    newUploadMultiSourceCallback: (params?: {
      fileIds?: string[];
      isDragged?: boolean;
      isFromPersonalize?: boolean;
      sourceType?: string;
    }) => {},
    onPressOptionCallback: (option: string) => {},
    /* MODAL OVER LIMIT PRODUCT */
    onPressRemoveProduct: () => {},
    onPressAddBox: () => {},
  }))
  .actions(self => {
    return {
      setDynamicModalView: (dynamicModalView: DynamicModalLayerInstance['dynamicModalView']) => {
        self.dynamicModalView = dynamicModalView;
      },
      setIsUploadingPhoto: (value: boolean) => {
        self.isUploadingPhoto = value;
      },
      setSelectedItem: (item: SELECTED_MENU_ITEMS) => {
        self.selectedMenuItem = item;
      },
      init: (options: AttachEventParams) => {
        self.onPressDeleteProduct = options.onPressDeleteProduct ?? (() => {});
        self.onPressDiscardChanges = options.onPressDiscardChanges ?? (() => {});
        self.onPressSaveChanges = options.onPressSaveChanges ?? (() => {});
        self.onPressContinueToCart = options.onPressContinueToCart ?? (() => {});
        self.uploadMultiSourceCallback = options.uploadMultiSourceCallback ?? (() => {});
        self.newUploadMultiSourceCallback = options.newUploadMultiSourceCallback ?? (() => {});
        self.actionCallback = options.actionCallback ?? (() => {});
        self.onPressOptionCallback = options.onPressOptionCallback ?? (() => {});
        self.onPressRemoveProduct = options.onPressRemoveProducts ?? (() => {});
        self.onPressAddBox = options.onPressAddBox ?? (() => {});

        if (options.afterInit) {
          options.afterInit();
        }
      },

      beforeDestroy() {
        // Clean up safeReference before destruction
        self.qualityAlertUserImage = undefined;

        if (__DEV__) console.log('[DynamicModalLayer] references cleaned before destroy');
      },
    };
  });
